import MyFlow from "flow/MyFlow";

type SubscriberCallback = (data: string) => void;

export class LocalStorageChannel {
    subscribers: { [id: string]: SubscriberCallback[] } = {};
    private isConnected: boolean = false;
    private myFlow: MyFlow;

    setup = (myFlow: MyFlow) => {
        this.myFlow = myFlow;
    };

    connect = () => {
        if (!this.isConnected) {
            window.addEventListener("storage", this.onStorageEvent, false);
            this.isConnected = true;
            this.myFlow.log("Session channel connected");
        }
    };

    disconnect = () => {
        if (this.isConnected) {
            window.removeEventListener("storage", this.onStorageEvent, false);
            this.isConnected = false;
            this.myFlow.log("Session channel disconnected");
        }
    };

    private onStorageEvent = (event: StorageEvent) => {
        this.myFlow.debug("Session message received on" + event.key);
        this.myFlow.logObjectsDebug(event.newValue);
        const topic = event.key;
        if (topic && this.subscribers[topic]) {
            this.subscribers[topic].forEach(s => s(event.newValue));
        }
    };

    subscribe = (topic: string, callback: SubscriberCallback) => {
        if (!this.isConnected) {
            this.connect();
        }

        if (!this.subscribers[topic]) {
            this.subscribers[topic] = [];
        }

        this.subscribers[topic].push(callback);

        this.myFlow.debug("Session message subscribed to " + topic);
    };

    unsubscribe = (topic: string, callback: SubscriberCallback) => {
        const topicSubscribers = this.subscribers[topic];
        if (!topicSubscribers) {
            return;
        }

        const index = topicSubscribers.indexOf(callback);
        if (index) {
            topicSubscribers.splice(index, 1);
            if (topicSubscribers.length === 0) {
                delete this.subscribers[topic];
                if (Object.keys(this.subscribers).length === 0) {
                    this.disconnect();
                }
            }

            this.myFlow.debug("Session message unsubscribed from " + topic);
        }
    };

    publish = (topic: string, message: string) => {
        this.myFlow.debug("Session message published on " + topic);
        this.myFlow.logObjectsDebug(message);
        window.localStorage.setItem(topic, message);
    };

    flash = (topic: string, message: string) => {
        this.myFlow.debug("Session message flashed on " + topic);
        this.myFlow.logObjectsDebug(message);
        window.localStorage.setItem(topic, message);
        window.localStorage.removeItem(topic);
    };

    clear = (topic: string) => {
        window.localStorage.removeItem(topic);
    };

    query = (
        topic: string,
        message: string,
        timeout: number,
        filter?: (message: string) => boolean
    ): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            const unsubscribe = this.unsubscribe;

            function onResponse(data: string) {
                if (filter) {
                    if (filter(data)) {
                        unsubscribe(topic, onResponse);
                        resolve(data);
                    }
                } else {
                    unsubscribe(topic, onResponse);
                    resolve(data);
                }
            }

            this.subscribe(topic, onResponse);

            window.setTimeout(() => {
                this.unsubscribe(topic, onResponse);
                reject();
            }, timeout);

            this.publish(topic, message);
        });
    };

    getCurrentValue = (topic: string) => {
        return window.localStorage.getItem(topic);
    };
}

const localStorageChannel = new LocalStorageChannel();
export default localStorageChannel;
